import { Modal } from "./Modal";
import { Button } from "./Button";

type Severity = "danger" | "warning" | "default";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  loading?: boolean;
  /** Visual emphasis for the action.
   *   - `danger` (default): red Delete-style confirm. Use for irreversible
   *     destructive actions (revoke, delete).
   *   - `warning`: amber confirm button + warning glyph next to the title.
   *     Use for reversible-but-disruptive actions (reboot, power-off) so the
   *     operator can't fire one by misclick.
   *   - `default`: neutral primary confirm. Use for low-risk affirmations. */
  severity?: Severity;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Delete",
  loading = false,
  severity = "danger",
}: ConfirmDialogProps) {
  const showIcon = severity === "warning" || severity === "danger";
  const confirmVariant: "primary" | "danger" | "warning" =
    severity === "warning" ? "warning" : severity === "danger" ? "danger" : "primary";
  const iconTone = severity === "warning" ? "text-amber-500" : "text-red-500";

  const heading = showIcon ? (
    <span className="flex items-center gap-2">
      <WarningIcon className={iconTone} />
      <span>{title}</span>
    </span>
  ) : (
    title
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={heading}>
      <p className="text-sm text-gray-600 mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant={confirmVariant} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={`inline-block w-5 h-5 ${className ?? ""}`}
    >
      <path
        fillRule="evenodd"
        d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.93.93 0 100-1.86.93.93 0 000 1.86z"
        clipRule="evenodd"
      />
    </svg>
  );
}
